/**
 * Хореография демо-сцены — ТОЖЕ ДАННЫЕ.
 *
 * Прежняя сцена держала полторы тысячи строк CSS: два десятка кейфреймов на
 * появление отдельных плашек и кадры камеры, вбитые числами. Поменять момент
 * или порядок было нельзя — надо было пересчитывать проценты руками в каждом
 * из них.
 *
 * Здесь всё выведено из двух таблиц:
 *   SHOTS — куда и когда смотрит камера (кадр берётся из ZONES, а не из чисел);
 *   BEATS — что и когда появляется, гаснет, вспыхивает, течёт или едет.
 * CSS собирается из них функцией `buildTimelineCss()` и вставляется в <style>
 * внутри сцены. Сдвинуть такт = поменять одно число в таблице.
 *
 * ── Что рассказывает сцена ──────────────────────────────────────────────────
 * Полный путь работы, без пропусков и догадок:
 *   1. Пространство уже живёт: доска, чат и База знаний со СВОИМ содержимым,
 *      к теме отношения не имеющим. Доска при этом ПУСТАЯ.
 *   2. Команда разбирает тему прямо на доске: формулирует проблему, приносит
 *      данные, достаёт описание процесса из Базы знаний, спорит.
 *   3. Задачу СОЗДАЮТ из доски: выделяют элементы, жмут «Создать задачу» — и
 *      она в тот же миг появляется карточкой на доске, страницей задачи и
 *      строкой в бэклоге. Это один объект, а не три записи.
 *   4. Исполнителем назначают агента.
 *   5. Агент читает приложенное, ищет по смыслу связанные обсуждения, находит
 *      запрос клиента полугодовой давности и переписку с поддержкой —
 *      прикладывает. Находит устаревший регламент — НЕ прикладывает и говорит,
 *      почему. Камера всё это время стоит на нём, а пространство за кадром
 *      оплетается сетью MCP: отвод за отводом в каждый раздел.
 *   6. «Фиксирую ход работы» — отъезд, вся сеть видна разом, гаснет.
 *   7. Агент заводит СВОЮ доску под страницей задачи: ход работы, ход
 *      рассуждений, разбор артефактов, обновлённая карта историй и фрагмент
 *      документации, который он поправил в Базе знаний. Наполнив её,
 *      схлопывает доску в задачу — вложением «Ход работы агента».
 *   8. И последнее, чем работа кончилась в коде: под задачей встаёт
 *      пулл-реквест с ветками, файлами и цифрами правок.
 *
 * ── Границы, которые нельзя нарушать ────────────────────────────────────────
 * Цикл 96.5 с и ЗАМКНУТ: кадр на 84.9-й секунде обязан совпадать с кадром на 0-й,
 * иначе на стыке итераций рывок. Поэтому первый и последний кадр камеры — один
 * и тот же (WORD_RECT).
 *
 * Слой контента гасится отдельной анимацией `dmWorldOut` (см. prelaunch-landing
 * .css): он непрозрачен с 2.8 с по 85.08 с. Вся хореография обязана уложиться
 * в этот промежуток — такт снаружи просто не будет виден. За пределами живёт
 * финал: слово PRONA держится 0…2.6 с (хвост предыдущего цикла) и собирается
 * заново с 85.1 с.

 * Слово PRONA держится 1.5 с и растворяется — это ЗАДУМАННОЕ начало показа, а не
 * хвост чужого цикла: тот же кадр закрывает цикл, поэтому стык бесшовный.
 */

import { ZONES, shot } from './demo-scene-layout';

/** Длина цикла. Совпадает с `animation-duration` всех кейфреймов сцены. */
export const TOTAL = 96.5;

/** Окно сцены — то же, что STAGE_W×STAGE_H в герое. */
export const STAGE = { w: 900, h: 600 } as const;

/** Окно видимости слоя контента (dmWorldOut). Такты вне него не видны. */
export const CONTENT_WINDOW = { from: 2.8, to: 85.08 } as const;

const pct = (t: number): string => `${+((t / TOTAL) * 100).toFixed(3)}%`;

/**
 * Прямоугольник слова PRONA в мировых координатах.
 *
 * ВНИМАНИЕ: должен совпадать с геометрией финала в PrelaunchLanding
 * (OUTRO_SRC = зона задачи, OUTRO_CELL/OUTRO_STEP). Слово встаёт по центру
 * карточки задачи, снизу — подпись, её тоже надо уместить в кадр, иначе камера
 * обрежет строку «Одно пространство · один источник данных».
 */
const OUTRO_CELL = 26;
const OUTRO_STEP = 33;
const OUTRO_WORD_COLS = 20;
const WORD_W = OUTRO_WORD_COLS * OUTRO_STEP - (OUTRO_STEP - OUTRO_CELL);

/**
 * ВЫСОТА КАДРА = высота карточки задачи, а не высота слова.
 *
 * В начале финала клетки стоят НА СВОИХ ДЕТАЛЯХ карточки — то есть занимают её
 * целиком, от шапки до последней строки ленты. Если кадрировать по слову
 * (214px), масштаб упирается в ширину, карточка перестаёт помещаться по высоте
 * и первые кадры взрыва режутся по краю. Замер минимальных полей давал −1.4px.
 * С высотой карточки кадр ограничен по вертикали, поля 6% — и осколки, и слово
 * помещаются с запасом.
 */
export const WORD_RECT = {
  x: ZONES.task.x + ZONES.task.w / 2 - WORD_W / 2,
  y: ZONES.task.y,
  w: WORD_W,
  h: ZONES.task.h,
};

type Rect = { x: number; y: number; w: number; h: number };

/**
 * Кадр камеры: приехать к моменту `at`, стоять до `till`.
 * Разгон/торможение — `ease` предыдущего переезда.
 */
type Shot = { at: number; till: number; rect: Rect; label: string };

/**
 * Кривые переездов камеры. Обе МЯГКИЕ НА КРАЯХ и крутые в середине: время
 * переезда прежнее, но старт и остановка перестали быть заметными.
 * У прежней EASE_PULL начальный наклон был 2.1 — камера срывалась с места и
 * читалась как рывок именно на дальних отъездах, где смещение и так велико.
 */
const EASE_MOVE = 'cubic-bezier(0.6, 0.02, 0.28, 1)';
const EASE_PULL = 'cubic-bezier(0.58, 0.02, 0.24, 1)';

/**
 * Появление и уход элементов. Раньше все такты шли по `linear` из объявления
 * анимации: плашка возникала с постоянной скоростью и «щёлкала» на краях.
 * Кривая мягко тормозит к концу — сохраняя ту же длительность.
 */
const EASE_IN_OUT = 'cubic-bezier(0.32, 0.72, 0.35, 1)';

/**
 * Страница задачи и окно агента за укоротившейся рамкой. Ширина 1260 — ТА ЖЕ,
 * что у кадра трёх вхождений, поэтому масштаб не меняется вовсе: между сценами
 * камера только едет вбок. Первый план агента держится его размером, а не зумом.
 *
 * Высота взята С ЗАПАСОМ ВНИЗ (до 760) намеренно: по зазору на 728 идёт шина
 * сети MCP, и в кадре агента должно быть видно, как нити уходят от шильдика
 * влево за край. Куда они пришли, покажет отъезд. Масштаб от этого не меняется —
 * кадр упирается в ширину.
 */
const AGENT_STAGE = { x: 980, y: 130, w: 1260, h: 630 };

/**
 * Карточка задачи НА ДОСКЕ и страница задачи — вплотную, без остальной канвы.
 * Кадр со всей доской (1260px, масштаб 0.67) показывал момент создания издали:
 * связь «эта карточка = эта страница» тонула среди стикеров и схемы. Здесь в
 * кадре только два предмета разговора, масштаб около единицы.
 */
const BOARD_TASK = { x: 600, y: 140, w: 800, h: 550 };

/**
 * Чат-сайдбар ВНУТРИ доски: кадр берёт правую треть канвы вместе с выехавшей
 * панелью и кнопкой вызова в нижнем углу — то есть и панель, и место, откуда
 * её открыли, и курсор агента, который туда нажимал.
 *
 * Пока чат был отдельным окном ПОД доской, сюда приходился съезд камеры вниз.
 * Теперь ехать некуда: панель встаёт на самой доске, и кадр только чуть
 * поджимается (0.96 → 1.03), чтобы переписка читалась. Съезд остался бы
 * движением в пустоту.
 */
const CHAT_PAN = { x: 570, y: 150, w: 430, h: 548 };

/** Доска целиком плюс панель Базы знаний. */
const KB_SHOT = { x: 120, y: 120, w: 1280, h: 580 };

/**
 * Три вхождения одной записи разом. Левая граница — ТА ЖЕ, что у ближнего кадра
 * создания задачи: в кадр входит карточка на канве, а не вся доска. Со всей
 * доской (1740px) масштаб падал до 0.49, и три подсвеченных места читались как
 * мелкие пятна; отсюда — 0.68 и видимое содержимое.
 */
const TRIO_SHOT = { x: 600, y: 120, w: 1260, h: 570 };
/** То же по ширине, кадр лишь опущен: в него входит и узел хранения. */
const TRIO_STORE_SHOT = { x: 600, y: 120, w: 1260, h: 810 };

/**
 * Отъезд на всю сцену — ради СЕТИ. Кадр показывают ровно один раз: когда агент
 * говорит «фиксирую ход работы», камера уходит назад, и видно то, что строилось
 * за кадром, — шину от MCP и отводы во все разделы пространства. Текст на этом
 * масштабе не читается, и читать его не надо: смотрят на связность.
 */
const NET_SHOT = { x: 20, y: 40, w: 2200, h: 740 };

/**
 * Схлопывание доски хода работы в задачу. В кадре обязаны быть ОБА конца полёта:
 * доска внизу и строка артефактов, в которую она уходит, — иначе видно только
 * «доска исчезла», а не «доска стала вложением задачи».
 */
const COLLAPSE_SHOT = { x: 1040, y: 150, w: 880, h: 1050 };

/**
 * Кадры камеры. Держать порядок по времени: генератор идёт по списку подряд и
 * ставит между кадрами переезд длиной (at следующего − till текущего).
 */
export const SHOTS: Shot[] = [
  { at: 0, till: 1.5, rect: WORD_RECT, label: 'слово (начало и конец цикла)' },
  // Доска грузится, выходят стикеры и курсоры — и ПАУЗА: зрителю дают прочитать
  // постановку до того, как на канве что-то задвигается.
  { at: 3.4, till: 11.2, rect: ZONES.board, label: 'команда разбирает тему' },
  { at: 12.2, till: 14.6, rect: KB_SHOT, label: 'агент идёт в Базу знаний' },
  // Камера возвращается на доску ВМЕСТЕ с документом: перенос заканчивается уже
  // в кадре доски, а не за его пределами.
  { at: 15.5, till: 21.1, rect: ZONES.board, label: 'Игорь читает документ и рисует схему' },
  // Чат выезжает сайдбаром ПО ПРАВОЙ КРОМКЕ САМОЙ ДОСКИ — ехать камере некуда,
  // она только поджимается к правой трети канвы, где панель и появилась.
  { at: 22.3, till: 25.3, rect: CHAT_PAN, label: 'агент ищет ответ в чате' },
  { at: 26.3, till: 32.1, rect: ZONES.board, label: 'ответ на доске, выделение' },
  // Сперва ДВЕ области вплотную: карточка задачи на канве и страница задачи.
  { at: 32.8, till: 35.4, rect: BOARD_TASK, label: 'задача открылась рядом' },
  // И только потом кадр отъезжает — в него входит бэклог.
  { at: 36.5, till: 38.9, rect: TRIO_SHOT, label: 'задача во всех трёх местах' },
  // Кадр держится ДОЛЬШЕ прочих: здесь единственное место, где сцена не
  // показывает действие, а объясняет устройство, — на чтение нужно время.
  // Обводка трёх вхождений, пунктиры и сам узел приходят ОДНИМ мигом (37.0):
  // поочерёдно это читалось как три разных события, а мысль ровно одна.
  { at: 39.8, till: 45, rect: TRIO_STORE_SHOT, label: 'представлений много, объект один' },
  // Назначение — КРУПНО НА СТРАНИЦЕ ЗАДАЧИ: выбор из списка иначе не разглядеть.
  { at: 46.4, till: 50, rect: ZONES.task, label: 'Игорь назначает исполнителя' },
  // …и сразу отъезд к трём вхождениям: исполнитель меняется во всех трёх разом.
  { at: 51.3, till: 53.1, rect: TRIO_SHOT, label: 'исполнитель поменялся везде' },
  // Тот же масштаб, что в предыдущем кадре (оба ограничены шириной 1260):
  // камера просто панорамирует вправо, а агент растёт САМ.
  // Камера СТОИТ НА АГЕНТЕ весь его ход рассуждений — почти восемнадцать
  // секунд. Прежде она вместо этого объезжала чат, Базу знаний и задачу, и
  // работа агента распадалась на четыре чужих кадра; теперь его findings видны
  // прямо здесь — в списке артефактов задачи, которая тут же, в кадре, — а куда
  // он ходил, рассказывает прорастающая сеть.
  { at: 54.1, till: 69.1, rect: AGENT_STAGE, label: 'агент рассуждает, сеть прорастает' },
  // «Фиксирую ход работы» — и отъезд: видно всю сеть Проны разом.
  { at: 70.4, till: 72.6, rect: NET_SHOT, label: 'сеть пространства целиком' },
  // Сеть гаснет, камера приходит на НОВУЮ доску под страницей задачи.
  { at: 73.9, till: 79.1, rect: ZONES.board2, label: 'доска хода работы' },
  // Отъезд к обоим концам полёта — и КАМЕРА БОЛЬШЕ НЕ ДВИГАЕТСЯ до самого
  // финала. Пулл-реквест приходит в этом же кадре: он встаёт вплотную под
  // страницей задачи, а она тут и так в кадре. Отдельный наезд на задачу с PR
  // стоял ровно перед взрывом карточки — камера успевала подъехать и тут же
  // уехать обратно, и предпоследний кадр читался как лишний рывок.
  { at: 80.2, till: 83.6, rect: COLLAPSE_SHOT, label: 'доска схлопывается в задачу, приходит PR' },
  { at: 84.9, till: TOTAL, rect: WORD_RECT, label: 'финал' },
];

/** Как элемент входит в кадр и уходит из него. */
export type BeatKind =
  /** Проявиться со сдвигом снизу — для карточек и блоков. */
  | 'rise'
  /** Просто проявиться — для линий, подписей, курсоров (у них занят transform). */
  | 'fade'
  /** Проявиться с подскоком — для значков, пилюль и кнопок. */
  | 'pop'
  /** Выехать справа — для сайдбара, пристыкованного к правой кромке окна. */
  | 'slide'
  /** Вспышка-обводка в момент `in`, без появления/исчезания. */
  | 'glow'
  /** Пунктир: проявиться и бесконечно течь штрихами. */
  | 'flow'
  /** Нить сети: прочерчивается от начала к концу и потом гаснет. */
  | 'wire'
  /** Сдвиг по вертикали в момент `in` — «строка вставилась, остальные съехали». */
  | 'shift'
  /** Проезд курсора по точкам маршрута. */
  | 'path'
  /** Побуквенный набор текста: ширина растёт ступенями по числу знаков. */
  | 'type'
  /** Рамка выделения: растёт из левого верхнего угла вслед за курсором. */
  | 'draw'
  /** Смена размера по шагам — рамка пространства под панели и без них. */
  | 'grow'
  /** Расходящееся кольцо: привлечь внимание к точке, не двигая её. */
  | 'ripple'
  /** Появиться, а в конце схлопнуться и улететь в другое место сцены. */
  | 'collapse';

export type Beat = {
  /** CSS-селектор внутри сцены (класс элемента). */
  sel: string;
  /** Момент появления, с. */
  in?: number;
  /** Момент ухода, с. Без него элемент остаётся до конца слоя. */
  out?: number;
  kind?: BeatKind;
  /** Длительность входа, с. */
  d?: number;
  /** Для 'shift' — на сколько пикселей элемент стоит выше до вставки. */
  dy?: number;
  /**
   * Для 'path' — маршрут: [секунда ПРИБЫТИЯ, смещение X, смещение Y].
   * Курсор стоит на прежнем месте и трогается только за `moveDur` до срока —
   * получается резкий бросок и остановка, как у живой мыши. Раньше он полз от
   * точки к точке всё время между ними, и движение выглядело ватным.
   */
  points?: [number, number, number][];
  /** Для 'path' — сколько длится сам бросок, с. */
  moveDur?: number;
  /** Для 'type' — число знаков: по нему считаются ступени и длительность. */
  chars?: number;
  /** Для 'grow' — шаги высоты: [секунда прибытия, высота px]. */
  hs?: [number, number][];
  /** Для 'grow' — шаги ширины: [секунда прибытия, ширина px]. */
  ws?: [number, number][];
  /**
   * Несколько интервалов показа одним тактом: [появиться, уйти].
   * Разными тактами нельзя — они правят одни и те же свойства, и последний
   * снёс бы предыдущие. Нужно панелям, которые открываются не один раз.
   */
  wins?: [number, number][];
  /**
   * Для 'glow' — несколько вспышек одним тактом. Разными тактами нельзя:
   * обе анимации правят box-shadow, и вторая (у которой до своего момента
   * стоит «тени нет») перекрывала бы первую.
   */
  ats?: number[];
  /**
   * Для 'collapse' — куда улетает центр элемента (мировые пиксели) и до какого
   * масштаба он ужимается. Появление и схлопывание — ОДИН такт: оба правят и
   * прозрачность, и transform, разными тактами второй снёс бы первый.
   */
  flyX?: number;
  flyY?: number;
  scale?: number;
  /** Для 'collapse' — сколько длится сам полёт, с. */
  outD?: number;
};

/**
 * Такты сцены. Порядок в списке роли не играет — только времена.
 * Разбивка по актам — в шапке файла.
 */
export const BEATS: Beat[] = [
  // ══ Акт 0. Доска грузится и открывается с готовой постановкой ═══════════
  { sel: '.dm-frame', in: 3, kind: 'fade', d: 0.6 },
  // Высота рамки ПОСТОЯННА: чат больше не отдельное окно снизу, а сайдбар самой
  // доски, и тянуть рамку вниз незачем.
  // ПРАВАЯ граница ужимается до страницы задачи, когда бэклог закрыт: рабочих
  // окон справа больше нет, а место нужно окну агента — оно выходит на первый
  // план собственным размером, а не приближением камеры.
  // …и снова расширяется — вправо и вниз, — когда агент заводит СВОЮ доску под
  // страницей задачи: пространство растёт под новую доску, а не ужимает её.
  // …а после того как доска схлопнулась в задачу, ужимается обратно: место
  // больше не нужно, вложение осталось внутри задачи.
  { sel: '.dm-frame-box', kind: 'grow', d: 0.5, ws: [
    [3, 1860], [54.1, 1380], [73.2, 1920], [82, 1420],
  ] },
  { sel: '.dm-frame-box', kind: 'grow', d: 0.5, hs: [
    [3, 700], [73.2, 1180], [82, 860],
  ] },
  // (такт ухода доски — в акте 9: она держится до самой смены на доску отчёта)
  // Доска СНАЧАЛА ГРУЗИТСЯ — тем же лоадером, что в продукте.
  { sel: '.dm-board-loader', in: 3.3, out: 4.5, kind: 'fade', d: 0.3 },
  // Сразу после загрузки — постановка и вся команда РАЗОМ, дальше пауза на
  // чтение: курсоры не должны работать поверх текста, который ещё не прочитан.
  { sel: '.dm-bm-title', in: 4.8, kind: 'fade', d: 0.3 },
  { sel: '.dm-bm-s1', in: 4.9, kind: 'fade', d: 0.3 },
  { sel: '.dm-bm-s2', in: 5, kind: 'fade', d: 0.3 },
  { sel: '.dm-bm-s3', in: 5.1, kind: 'fade', d: 0.3 },

  // ══ Акт 1. Аня приносит данные и набирает вопрос ════════════════════════
  { sel: '.dm-c-anya', in: 4.9, out: 32.1, kind: 'fade', d: 0.25 },
  { sel: '.dm-c-anya', kind: 'path', points: [
    [4.9, -14, 10], [8, 80, 160], [11, 530, 10],
    // Выделение: курсор ведёт рамку по диагонали, точки идут часто — с шагом
    // чуть больше броска ход получается непрерывным, без рывков.
    // Последняя точка протяжки — РОВНО правый нижний угол рамки (мир 980×514),
    // а не «примерно там»: иначе рамка дорисовывается сама, без курсора.
    [28.6, -58, -108], [29.1, 152, -27], [29.5, 365, 57], [29.9, 577, 140],
    [30.3, 790, 224],
    // …и курсор переходит В ЦЕНТР кнопки «Создать задачу» (мир 910×533) и
    // нажимает. Раньше он останавливался рядом, и нажатия видно не было.
    [31.1, 718, 241], [32.1, 718, 241],
  ] },
  { sel: '.dm-bm-tbl', in: 7.2, kind: 'fade', d: 0.25 },
  // Заметка появляется ПУСТОЙ — с одной кареткой; текст набирается построчно.
  { sel: '.dm-bm-note', in: 8, kind: 'fade', d: 0.25 },
  { sel: '.dm-note-l1', in: 8.2, kind: 'type', chars: 15 },
  { sel: '.dm-note-l2', in: 8.9, kind: 'type', chars: 20 },
  { sel: '.dm-note-l3', in: 9.7, kind: 'type', chars: 9 },

  // ══ Акт 2. Агент приносит документацию ПЕРВЫМ ═══════════════════════════
  // Порядок важен: сперва на доске появляется описание процесса, и только потом
  // Игорь его читает и рисует по нему зависимости. В обратном порядке схема
  // возникала из ниоткуда — непонятно, на чём она основана.
  { sel: '.dm-c-lena', in: 4.9, out: 29.1, kind: 'fade', d: 0.25 },
  { sel: '.dm-c-lena', kind: 'path', moveDur: 0.45, points: [
    [4.9, 320, -5], [11, 745, 22], [13.2, 920, -160], [15.5, 170, -30],
    // К КНОПКЕ ЧАТА в правом нижнем углу канвы (мир 963×663) — и нажатие.
    [21.3, 721, 231], [23.1, 721, 231],
    // В сайдбаре курсор НЕ СТОИТ: проходит строку поиска, ведёт по переписке и
    // упирается во вложение — видно, что агент читает, а не просто «оказался».
    [23.9, 518, -230], [24.7, 528, -140], [25.3, 528, -70],
    // …и возвращается на доску, чтобы положить ответ поверх вопроса. Встаёт
    // ПОД стикером, а не на нём: стрелка с плашкой имени закрывала ровно те
    // строки, которые в этот момент набираются.
    [26.5, -64, 92], [29.1, -64, 92],
  ] },
  { sel: '.dm-say-l1', in: 7.4, out: 9.3, kind: 'fade', d: 0.18 },
  { sel: '.dm-say-l1', in: 7.5, kind: 'type', chars: 32, d: 0.9 },

  // Ярлык-корешок Базы знаний загорается, только КОГДА агент подъехал к кромке.
  // Возвращается он ещё раз — как ТОЧКА ПОДКЛЮЧЕНИЯ сети MCP: в неё приходит
  // отвод, и раздел числится связанным, пока сеть жива.
  { sel: '.dm-invite-kb', kind: 'pop', d: 0.25, wins: [[10.6, 12.2], [59.9, 76.1]] },
  { sel: '.dm-invite-kb', kind: 'glow', ats: [11.2, 61.4] },
  // Панель Базы знаний открывается ОДИН РАЗ — когда её зовёт агент платформы.
  // Второе открытие (внешний агент проверяет документы) снято: панель делит слот
  // со страницей задачи и накрыла бы её ровно в тот момент, когда камера стоит
  // на агенте и в кадре должен быть список артефактов задачи.
  { sel: '.dm-w-kb', kind: 'pop', d: 0.35, wins: [[11.6, 15.9]] },
  { sel: '.dm-kb-tree', in: 12.3, kind: 'fade', d: 0.25 },
  { sel: '.dm-kb-pageA', in: 12.3, out: 12.7, kind: 'fade', d: 0.2 },
  { sel: '.dm-kb-pageB', in: 12.9, kind: 'fade', d: 0.25 },
  { sel: '.dm-kb-row-cart', in: 13.1, kind: 'glow' },
  { sel: '.dm-kb-pageB', kind: 'glow', ats: [13.4] },
  { sel: '.dm-drag-doc', in: 14.1, out: 15.6, kind: 'fade', d: 0.18 },
  { sel: '.dm-drag-doc', kind: 'path', moveDur: 0.45, points: [[14.1, 774, -190], [15.5, 0, 0]] },
  { sel: '.dm-bm-doc', in: 15.5, kind: 'fade', d: 0.25 },

  // ══ Акт 3. Игорь читает документ и рисует зависимости ═══════════════════
  // Курсор проходит по листу — это и есть «прочитал»; без этого схема выглядела
  // бы догадкой, а не выводом из документа.
  { sel: '.dm-c-igor', in: 4.9, out: 52.9, kind: 'fade', d: 0.25 },
  { sel: '.dm-c-igor', kind: 'path', moveDur: 0.26, points: [
    [4.9, -226, 16], [16.3, -54, 192], [17.1, 70, 192],
    [17.8, -220, 20], [18.2, -220, 40], [18.6, -152, 42], [18.9, -128, 40],
    [19.3, -60, 42], [19.6, -36, 40], [20.5, 290, 10],
    // Поле «Исполнитель» на странице задачи, затем строка «Claude Code»
    // в раскрытом списке — камера в этот момент стоит крупно на задаче.
    [46.5, 664, 16], [48.2, 664, 113], [52.9, 720, 290],
  ] },
  { sel: '.dm-sch-lbl', in: 17.7, kind: 'fade', d: 0.2 },
  { sel: '.dm-sch-b1', in: 18.2, kind: 'pop', d: 0.2 },
  { sel: '.dm-sch-a1', in: 18.6, kind: 'fade', d: 0.18 },
  { sel: '.dm-sch-b2', in: 18.9, kind: 'pop', d: 0.2 },
  { sel: '.dm-sch-a2', in: 19.3, kind: 'fade', d: 0.18 },
  { sel: '.dm-sch-b3', in: 19.6, kind: 'pop', d: 0.2 },

  // ══ Акт 4. Агент ищет ответ в переписке и пишет его на доску ════════════
  // Кнопка чата живёт на канве ПОСТОЯННО, вместе с доской: это часть рабочего
  // полотна, а не приглашение, выскакивающее под сценарий.
  { sel: '.dm-invite-chat', in: 4.9, kind: 'pop', d: 0.3 },
  // Второй раз кнопка загорается, когда в неё приходит отвод сети MCP.
  { sel: '.dm-invite-chat', kind: 'glow', ats: [21.5, 64.2] },
  // Нажатие по кнопке — кольцами: курсор стоит на месте, и «нажал» иначе
  // неотличимо от «подвёл».
  { sel: '.dm-rip-h1', in: 21.6, kind: 'ripple' },
  { sel: '.dm-rip-h2', in: 21.75, kind: 'ripple' },
  { sel: '.dm-rip-h3', in: 22.3, kind: 'ripple' },
  // Сайдбар ВЫЕЗЖАЕТ вдоль правой кромки доски, а не проявляется на месте.
  // Сайдбар открывается ОДИН РАЗ. Второе открытие (внешний агент ищет исходный
  // запрос) снято: панель легла бы поверх кнопки чата и корешка Базы знаний —
  // а это точки, куда в тот момент приходят отводы сети. Что агент нашёл в
  // переписке, видно там же, где это и должно быть видно, — в списке артефактов
  // задачи, которая в этот момент в кадре.
  { sel: '.dm-w-chat', kind: 'slide', d: 0.4, wins: [[22.5, 27.7]] },
  { sel: '.dm-ch-now', kind: 'fade', d: 0.25, wins: [[22.7, 23.7]] },
  { sel: '.dm-ch-search', kind: 'fade', d: 0.25, wins: [[23.7, 27.7]] },
  { sel: '.dm-ch-old', kind: 'fade', d: 0.3, wins: [[24.1, 27.7]] },
  { sel: '.dm-ch-msg', kind: 'glow', ats: [24.5] },
  { sel: '.dm-ch-att', kind: 'glow', ats: [25.1] },
  // Ответ ложится ПОВЕРХ вопроса и НАБИРАЕТСЯ так же, как набирали вопрос.
  { sel: '.dm-bm-answer', in: 26.7 },
  { sel: '.dm-ans-l1', in: 27, kind: 'type', chars: 23 },
  { sel: '.dm-ans-l2', in: 27.9, kind: 'type', chars: 20 },

  // ══ Акт 5. Задачу создают ИЗ ДОСКИ ══════════════════════════════════════
  { sel: '.dm-lasso', in: 28.9, out: 31.9, kind: 'draw', d: 1.4 },
  { sel: '.dm-lasso-btn', in: 30.5, out: 31.9, kind: 'pop', d: 0.25 },
  // Курсор дошёл до центра кнопки к 29.4 — и жмёт. Задача рождается на 30.2,
  // то есть ПОСЛЕ нажатия, а не одновременно с ним.
  { sel: '.dm-rip-c1', in: 31.2, kind: 'ripple' },
  { sel: '.dm-rip-c2', in: 31.35, kind: 'ripple' },
  { sel: '.dm-rip-c3', in: 31.5, kind: 'ripple' },

  { sel: '.dm-b-task', in: 31.9 },
  { sel: '.dm-w-task', in: 32.2, kind: 'pop', d: 0.35 },
  { sel: '.dm-t-head', in: 33, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-desc', in: 33.2, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-fields', in: 33.4, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-arts', in: 33.6, kind: 'fade', d: 0.25 },
  { sel: '.dm-t-a1', in: 33.8, d: 0.25 },
  { sel: '.dm-t-a2', in: 34, d: 0.25 },
  { sel: '.dm-t-a3', in: 34.2, d: 0.25 },
  { sel: '.dm-t-feed', in: 34.4, kind: 'fade', d: 0.25 },
  { sel: '.dm-t-f1', in: 34.6, kind: 'fade', d: 0.25 },

  // Бэклог появляется, когда кадр отъехал и вмещает три вхождения.
  { sel: '.dm-w-backlog', in: 35.8, out: 53.1, kind: 'pop', d: 0.35 },
  { sel: '.dm-bl-head', in: 36.6, kind: 'fade', d: 0.25 },
  { sel: '.dm-bl-rows', in: 36.7, kind: 'fade', d: 0.25 },
  { sel: '.dm-bl-row214', in: 37.1, d: 0.25 },
  { sel: '.dm-bl-rows', in: 37.1, kind: 'shift', dy: -28, d: 0.25 },
  { sel: '.dm-bl-cnt5', out: 37.4, kind: 'fade', d: 0.18 },
  { sel: '.dm-bl-cnt6', in: 37.6, kind: 'fade', d: 0.18 },

  // ══ Акт 6. Один объект в хранении ═══════════════════════════════════════
  // ВСЁ РАЗОМ, одним мигом: синяя обводка на трёх вхождениях, пунктиры к узлу и
  // сам узел. Поочерёдно (сначала обводка, через три секунды линии) это читалось
  // как три отдельных события, хотя мысль ровно одна — запись одна.
  { sel: '.dm-b-task', kind: 'glow', ats: [40.2, 51.6] },
  // Третья вспышка на задаче — когда в неё приходит отвод сети MCP.
  { sel: '.dm-t-head', kind: 'glow', ats: [40.2, 51.6, 59.2] },
  { sel: '.dm-bl-row214', kind: 'glow', ats: [40.2, 51.6] },
  { sel: '.dm-store-links', in: 40.2, out: 44.2, kind: 'flow' },
  { sel: '.dm-store', in: 40.2, out: 44.4, kind: 'rise', d: 0.45 },

  // ══ Акт 7. Игорь назначает исполнителя ══════════════════════════════════
  // Выбор — КРУПНО НА СТРАНИЦЕ ЗАДАЧИ (кадр 42.8…46.4): список из четырёх строк
  // на общем плане не читался, и «выбрал агента» приходилось додумывать.
  { sel: '.dm-t-asg-list', in: 47, out: 48.8, kind: 'pop', d: 0.22 },
  { sel: '.dm-t-asg-empty', out: 48.8, kind: 'fade', d: 0.2 },
  { sel: '.dm-t-asg-agent', in: 48.9, kind: 'pop', d: 0.3 },
  { sel: '.dm-t-asg-field', in: 48.9, kind: 'glow' },
  { sel: '.dm-t-st-backlog', out: 49.2, kind: 'fade', d: 0.2 },
  { sel: '.dm-t-st-work', in: 49.4, out: 71.4, kind: 'pop', d: 0.3 },
  // …и только теперь, на отъехавшем кадре с тремя вхождениями, исполнитель
  // проступает в остальных двух местах — с кольцами сразу во всех трёх.
  { sel: '.dm-b-task-empty', out: 51.5, kind: 'fade', d: 0.2 },
  { sel: '.dm-b-task-agent', in: 51.6, kind: 'fade', d: 0.3 },
  { sel: '.dm-bl-ava', in: 51.7, kind: 'pop', d: 0.3 },
  { sel: '.dm-rip-1', in: 51.6, kind: 'ripple' },
  { sel: '.dm-rip-2', in: 51.85, kind: 'ripple' },
  { sel: '.dm-rip-3', in: 52.1, kind: 'ripple' },
  { sel: '.dm-t-f2', in: 52, kind: 'fade', d: 0.25 },

  // ══ Акт 8. Агент рассуждает, а пространство оплетается сетью ════════════
  // Камера ВСЁ ЭТО ВРЕМЯ стоит на агенте. Каждая его реплика подкреплена двумя
  // вещами В КАДРЕ: строкой в списке артефактов задачи (она рядом) и новым
  // отводом сети, уходящим влево за край. Куда отводы пришли — покажет отъезд.
  { sel: '.dm-agent', in: 54.5, kind: 'pop', d: 0.5 },
  { sel: '.dm-mcp', in: 55, kind: 'pop', d: 0.3 },
  { sel: '.dm-mcp-line', in: 55.2, kind: 'flow' },
  { sel: '.dm-ag-1', in: 55.7, kind: 'fade', d: 0.3 },

  { sel: '.dm-ag-2', in: 57.1, kind: 'fade', d: 0.3 },
  // Отвод 1 — в страницу задачи.
  { sel: '.dm-net-r1', in: 57.5, out: 71.8, kind: 'wire', d: 1.0 },
  { sel: '.dm-net-p1', in: 58.1, out: 71.6, kind: 'flow' },
  { sel: '.dm-net-e1', in: 58.4, out: 71.8, kind: 'pop', d: 0.3 },

  { sel: '.dm-ag-3', in: 58.7, kind: 'fade', d: 0.3 },
  // Отвод 2 — в корешок Базы знаний на кромке доски.
  { sel: '.dm-net-r2', in: 59.1, out: 71.8, kind: 'wire', d: 1.0 },
  { sel: '.dm-net-p2', in: 59.7, out: 71.6, kind: 'flow' },
  { sel: '.dm-net-e2', in: 60, out: 71.8, kind: 'pop', d: 0.3 },

  { sel: '.dm-ag-4', in: 60.3, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-a4', in: 60.7, d: 0.3 },
  // Отвод 3 — в кнопку чата.
  { sel: '.dm-net-r3', in: 61.1, out: 71.8, kind: 'wire', d: 1.0 },
  { sel: '.dm-net-p3', in: 61.7, out: 71.6, kind: 'flow' },
  { sel: '.dm-net-e3', in: 62, out: 71.8, kind: 'pop', d: 0.3 },

  { sel: '.dm-ag-5', in: 62.3, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-a5', in: 62.7, d: 0.3 },
  { sel: '.dm-ag-6', in: 63.9, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-a6', in: 64.3, d: 0.3 },
  // Отвод 4 — в саму доску, далеко влево: сеть охватывает пространство, а не
  // жмётся к одному его краю.
  { sel: '.dm-net-r4', in: 64.3, out: 71.8, kind: 'wire', d: 1.3 },
  { sel: '.dm-net-p4', in: 65.3, out: 71.6, kind: 'flow' },
  { sel: '.dm-net-e4', in: 65.6, out: 71.8, kind: 'pop', d: 0.3 },

  { sel: '.dm-ag-7', in: 65.9, kind: 'fade', d: 0.3 },
  { sel: '.dm-t-f3', in: 66.5, kind: 'fade', d: 0.25 },
  { sel: '.dm-t-st-done', in: 67.3, kind: 'pop', d: 0.3 },
  // «Фиксирую ход работы» — реплика, по которой камера уходит на общий план.
  { sel: '.dm-ag-8', in: 67.3, kind: 'fade', d: 0.3 },

  // ══ Акт 9. Отъезд: сеть Проны целиком, потом она гаснет ═════════════════
  // Отводы гаснут на 66.8 — уже НА общем плане, зритель успевает увидеть сеть
  // целиком. Рабочая доска остаётся до конца: доска хода работы — ОТДЕЛЬНАЯ и
  // стоит ниже, так что подменять ею рабочую больше не нужно.
  { sel: '.dm-w-board', in: 3.2 },

  // ══ Акт 10. Агент заводит свою доску и схлопывает её в задачу ═══════════
  // Появление и полёт — ОДИН такт: оба правят transform. Центр доски уходит на
  // (−278, −416) — ровно в середину седьмой строки артефактов задачи.
  { sel: '.dm-w-board2', in: 73.2, out: 80.6, kind: 'collapse', d: 0.4,
    flyX: -278, flyY: -416, scale: 0.06, outD: 1.0 },
  { sel: '.dm-r-s1', in: 74.3, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-s2', in: 74.5, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-s3', in: 74.7, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-s4', in: 74.9, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-s5', in: 75.1, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-n1', in: 75.4, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-n2', in: 75.6, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-n3', in: 75.8, kind: 'fade', d: 0.25 },
  { sel: '.dm-r-v1', in: 76.1, d: 0.2 },
  { sel: '.dm-r-v2', in: 76.25, d: 0.2 },
  { sel: '.dm-r-v3', in: 76.4, d: 0.2 },
  { sel: '.dm-r-v4', in: 76.55, d: 0.2 },
  { sel: '.dm-r-v5', in: 76.7, d: 0.2 },
  { sel: '.dm-r-usm', in: 77, kind: 'fade', d: 0.28 },
  { sel: '.dm-r-usm-new', in: 77.3, kind: 'pop', d: 0.28 },
  { sel: '.dm-r-doc', in: 77.5, kind: 'fade', d: 0.28 },
  { sel: '.dm-r-doc-add', in: 77.8, kind: 'fade', d: 0.28 },
  // Доска долетела — и стала строкой вложения в задаче.
  { sel: '.dm-t-a7', in: 81.4, kind: 'pop', d: 0.35 },
  { sel: '.dm-t-a7', kind: 'glow', ats: [81.6] },

  // ══ Акт 11. Пулл-реквест с правками кода ════════════════════════════════
  { sel: '.dm-pr', in: 82, kind: 'rise', d: 0.4 },
  { sel: '.dm-pr-head', in: 82.3, kind: 'fade', d: 0.25 },
  { sel: '.dm-pr-f1', in: 82.5, kind: 'fade', d: 0.2 },
  { sel: '.dm-pr-f2', in: 82.65, kind: 'fade', d: 0.2 },
  { sel: '.dm-pr-f3', in: 82.8, kind: 'fade', d: 0.2 },
  { sel: '.dm-pr-f4', in: 82.95, kind: 'fade', d: 0.2 },
  { sel: '.dm-pr-foot', in: 83.2, kind: 'fade', d: 0.25 },
  // Обновлённый абзац в Базе знаний. Панель к этому моменту закрыта, так что на
  // экране он не появляется — такт держится ради обратного: он ГАРАНТИРУЕТ, что
  // правки агента не видно на раннем визите в Базу знаний, когда их ещё нет.
  // Саму правку показывает колонка «ДОКУМЕНТАЦИЯ В БЗ» на доске отчёта.
  { sel: '.dm-kb-diff', in: 81.2, kind: 'fade', d: 0.35 },
];



/* ────────────────────────────── Генератор CSS ────────────────────────────── */

const HIDDEN: Record<'rise' | 'fade' | 'pop' | 'slide', string> = {
  rise: 'opacity:0;transform:translateY(9px)',
  fade: 'opacity:0',
  pop: 'opacity:0;transform:scale(.9)',
  // Сдвиг РОВНО на свою ширину: створка уходит за правую кромку целиком, и
  // возвращается оттуда же. Меньший сдвиг читался бы как «подпрыгнула на месте».
  slide: 'opacity:0;transform:translateX(360px)',
};
const SHOWN: Record<'rise' | 'fade' | 'pop' | 'slide', string> = {
  rise: 'opacity:1;transform:none',
  fade: 'opacity:1',
  pop: 'opacity:1;transform:none',
  slide: 'opacity:1;transform:none',
};

function beatKeyframes(name: string, b: Beat): string {
  const kind = b.kind ?? 'rise';
  const d = b.d ?? 0.5;

  if (kind === 'glow') {
    // Вспышка обводкой: элемент никуда не двигается и не гаснет — только на
    // полторы секунды обрастает кольцом в свой акцент. Цвет берётся из
    // --glow, чтобы одна анимация обслуживала любые подсветки.
    //
    // Кольцо ВНУТРЕННЕЕ (inset). Наружное обрезалось бы: вхождения лежат внутри
    // окон, а у окна `overflow: hidden` ради скруглённых углов.
    const NONE = 'box-shadow:inset 0 0 0 0 rgba(0,0,0,0)';
    const RING = 'box-shadow:inset 0 0 0 2px var(--glow)';
    const times = b.ats ?? [b.in ?? 0];
    const stops = times.flatMap((t, i) => [
      `${i === 0 ? '0%,' : ''}${pct(t)}{${NONE}}`,
      `${pct(t + 0.35)}{${RING}}`,
      `${pct(t + 1.5)}{${RING}}`,
      `${pct(t + 2.1)}${i === times.length - 1 ? ',100%' : ''}{${NONE}}`,
    ]);
    return `@keyframes ${name}{${stops.join('')}}`;
  }

  if (kind === 'flow') {
    // Пунктир проявляется и дальше бесконечно течёт штрихами: линия читается
    // как поток, а не как статичная черта. Шаг штриха 12 (dasharray 6 6),
    // поэтому смещение кратно ему — иначе на стыке цикла штрихи прыгнут.
    const t = b.in ?? 0;
    // `out` обязателен к поддержке: пунктир, показанный один раз ради
    // объяснения, должен уйти — иначе он остаётся на сцене до конца цикла и
    // мешает читать следующие акты.
    const tail = b.out !== undefined
      ? `${pct(b.out)}{opacity:.55}${pct(b.out + 0.4)},100%{opacity:0}`
      : '100%{opacity:.55}';
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{opacity:0}`
      + `${pct(t + 0.8)}{opacity:.55}`
      + `${tail}}`
      // Бег штрихов — отдельным кейфреймом: он линейный и бесконечный, а
      // прозрачность живёт своим расписанием.
      + `@keyframes ${name}f{0%{stroke-dashoffset:0}100%{stroke-dashoffset:-720}}`;
  }

  if (kind === 'collapse') {
    // Доска наполняется, а в конце УЛЕТАЕТ вложением в задачу: ужимается почти
    // в точку и уходит к строке артефактов. Появление и полёт держит один такт —
    // оба правят transform, и разными тактами полёт снёс бы появление.
    const t = b.in ?? 0;
    const o = b.out ?? TOTAL;
    const od = b.outD ?? 1.0;
    const tf = `translate(${b.flyX ?? 0}px,${b.flyY ?? 0}px) scale(${b.scale ?? 0.06})`;
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{opacity:0;transform:scale(.97);animation-timing-function:${EASE_IN_OUT}}`
      + `${pct(t + d)}{opacity:1;transform:none}`
      + `${pct(o)}{opacity:1;transform:none;animation-timing-function:cubic-bezier(.6,.02,.4,1)}`
      + `${pct(o + od)},100%{opacity:0;transform:${tf}}}`;
  }

  if (kind === 'wire') {
    // Нить сети ПРОЧЕРЧИВАЕТСЯ: у пути задан pathLength=100 и dasharray=100,
    // поэтому смещение штриха от 100 к 0 открывает линию от начала к концу.
    // Байты по ней бегут ОТДЕЛЬНЫМ элементом (kind:'flow'): одной анимацией
    // «прочертить один раз» и «крутить поток вечно» не совместить.
    const t = b.in ?? 0;
    const dd = b.d ?? 1.0;
    const tail = b.out !== undefined
      ? `${pct(b.out)}{opacity:.5;stroke-dashoffset:0}${pct(b.out + 0.5)},100%{opacity:0;stroke-dashoffset:0}`
      : '100%{opacity:.5;stroke-dashoffset:0}';
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{opacity:0;stroke-dashoffset:100}`
      + `${pct(t + 0.15)}{opacity:.5;stroke-dashoffset:100}`
      + `${pct(t + dd)}{opacity:.5;stroke-dashoffset:0}`
      + tail + '}';
  }

  if (kind === 'shift') {
    const t = b.in ?? 0;
    const dy = b.dy ?? -28;
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{transform:translateY(${dy}px);animation-timing-function:${EASE_IN_OUT}}`
      + `${pct(t + d)},100%{transform:none}}`;
  }

  if (kind === 'path') {
    // Курсор ЖДЁТ на месте и трогается только за `moveDur` до срока прибытия.
    // Кривая резкая: быстрый старт, короткое дотягивание — так двигают мышь.
    const pts = b.points ?? [];
    const move = b.moveDur ?? 0.38;
    // Начальный наклон прежней кривой (0.22, 0.85) был 3.9 — курсор дёргался
    // с места. Здесь бросок такой же короткий, но трогается и встаёт мягко.
    const EASE = 'cubic-bezier(0.36,0.1,0.22,1)';
    const stops: string[] = [`0%{transform:translate(${pts[0][1]}px,${pts[0][2]}px)}`];
    pts.forEach(([t, x, y], i) => {
      if (i > 0) {
        const [, px0, py0] = pts[i - 1];
        stops.push(`${pct(Math.max(pts[i - 1][0], t - move))}{transform:translate(${px0}px,${py0}px);`
          + `animation-timing-function:${EASE}}`);
      }
      stops.push(`${pct(t)}{transform:translate(${x}px,${y}px)}`);
    });
    const last = pts[pts.length - 1];
    stops.push(`100%{transform:translate(${last[1]}px,${last[2]}px)}`);
    return `@keyframes ${name}{${stops.join('')}}`;
  }

  if (kind === 'grow') {
    // Высота меняется НЕ мгновенно и не всё время: элемент стоит на прежнем
    // значении и переезжает к новому за `d` до срока — та же механика, что у
    // маршрутов курсора, поэтому движение читается как одно действие.
    const prop = b.ws ? 'width' : 'height';
    const steps = b.ws ?? b.hs ?? [];
    const stops: string[] = [`0%{${prop}:${steps[0][1]}px}`];
    steps.forEach(([t, v], i) => {
      if (i > 0) {
        stops.push(`${pct(Math.max(steps[i - 1][0], t - d))}`
          + `{${prop}:${steps[i - 1][1]}px;animation-timing-function:${EASE_IN_OUT}}`);
      }
      stops.push(`${pct(t)}{${prop}:${v}px}`);
    });
    stops.push(`100%{${prop}:${steps[steps.length - 1][1]}px}`);
    return `@keyframes ${name}{${stops.join('')}}`;
  }

  if (kind === 'ripple') {
    // Кольцо расходится и гаснет. Держать дольше секунды нельзя: внимание
    // привлекает короткая вспышка, а долгая пульсация становится фоном.
    const t = b.in ?? 0;
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{opacity:0;transform:scale(.35)}`
      + `${pct(t + 0.06)}{opacity:.85;transform:scale(.35)}`
      + `${pct(t + 1.1)},100%{opacity:0;transform:scale(2.8)}}`;
  }

  if (kind === 'draw') {
    // Рамка ВЕДЁТСЯ курсором: растёт из угла, а не появляется готовой.
    // Начало координат — левый верхний угол, поэтому рамка тянется вправо-вниз,
    // ровно как при протяжке мышью.
    const t = b.in ?? 0;
    const stops = [`0%,${pct(t)}{opacity:0;transform:scale(0,0);`
      + `animation-timing-function:${EASE_IN_OUT}}`,
      `${pct(t + 0.12)}{opacity:1}`,
      `${pct(t + d)}{opacity:1;transform:none}`];
    if (b.out !== undefined) {
      stops.push(`${pct(b.out)}{opacity:1;transform:none}`,
        `${pct(b.out + 0.3)},100%{opacity:0;transform:none}`);
    } else {
      stops.push('100%{opacity:1;transform:none}');
    }
    return `@keyframes ${name}{${stops.join('')}}`;
  }

  if (kind === 'type') {
    // Набор текста: ширина растёт СТУПЕНЯМИ по числу знаков, поэтому буквы
    // появляются по одной, а не выезжают плавной шторкой.
    const t = b.in ?? 0;
    const n = Math.max(1, b.chars ?? 20);
    const dur = b.d ?? n * 0.035;
    // Реветь текст ШТОРКОЙ `clip-path`, а не ростом ширины.
    //
    // Ширина требовала ЗАРАНЕЕ знать, сколько пикселей займёт строка, и любая
    // ошибка оценки обрезала хвост насовсем — именно это и случилось с вопросом
    // на доске. Клип работает поверх естественной ширины строки: промахнуться
    // нечем, а ступени steps() по числу знаков дают ту же побуквенность.
    return `@keyframes ${name}{`
      + `0%,${pct(t)}{clip-path:inset(0 100% 0 0);animation-timing-function:steps(${n},end)}`
      + `${pct(t + dur)},100%{clip-path:inset(0 0 0 0)}}`;
  }

  const hid = HIDDEN[kind];
  const sho = SHOWN[kind];

  // Кривая ставится на стопе, С КОТОРОГО начинается движение: без неё интервал
  // шёл по `linear` из объявления анимации, и плашка возникала с постоянной
  // скоростью — на краях это читалось как щелчок.
  const ease = `;animation-timing-function:${EASE_IN_OUT}`;

  if (b.wins) {
    const parts = ['0%{' + hid + '}'];
    b.wins.forEach(([from, to]) => {
      parts.push(`${pct(from)}{${hid}${ease}}`, `${pct(from + d)}{${sho}}`,
                 `${pct(to)}{${sho}${ease}}`, `${pct(to + d)}{${hid}}`);
    });
    parts.push('100%{' + hid + '}');
    return `@keyframes ${name}{${parts.join('')}}`;
  }

  const stops: string[] = [];
  if (b.in !== undefined) {
    stops.push(`0%,${pct(b.in)}{${hid}${ease}}`);
    stops.push(`${pct(b.in + d)}{${sho}}`);
  } else {
    stops.push(`0%{${sho}}`);
  }
  if (b.out !== undefined) {
    stops.push(`${pct(b.out)}{${sho}${ease}}`);
    stops.push(`${pct(b.out + d)},100%{${hid}}`);
  } else {
    stops.push(`100%{${sho}}`);
  }
  return `@keyframes ${name}{${stops.join('')}}`;
}

/**
 * Кадры камеры. Между «стоянками» — переезд с разгоном и торможением;
 * на самой стоянке значение держится, поэтому кадр не «плывёт».
 */
function camKeyframes(name: string, stage: { w: number; h: number }): string {
  const stops: string[] = [];
  SHOTS.forEach((s, i) => {
    const c = shot(s.rect, stage);
    // ПОРЯДОК: scale ПЕРЕД translate. `shot()` возвращает сдвиг в МИРОВЫХ
    // единицах (tx = −(cx − (W/2)/s)), а CSS применяет список справа налево:
    // `scale(s) translate(t)` даёт s·(p+t) — ровно то, подо что выведена
    // формула. При обратном порядке вышло бы s·p+t, и кадр уезжал бы тем
    // сильнее, чем крупнее план.
    const tf = `transform:scale(${c.s}) translate(${c.tx}px,${c.ty}px)`;
    // Дальний переезд (смена масштаба больше чем в полтора раза) тормозим
    // мягче — иначе кадр «прилетает» и клюёт.
    const prev = SHOTS[i - 1];
    const far = prev
      ? Math.max(c.s, shot(prev.rect, stage).s) / Math.min(c.s, shot(prev.rect, stage).s) > 1.5
      : false;
    stops.push(`${pct(s.at)}{${tf};animation-timing-function:linear}`);
    if (s.till > s.at) {
      stops.push(`${pct(s.till)}{${tf};animation-timing-function:${far ? EASE_PULL : EASE_MOVE}}`);
    }
  });
  return `@keyframes ${name}{${stops.join('')}}`;
}

/**
 * Весь CSS сцены: кадры камеры + такты элементов.
 * `scope` — класс-обёртка, чтобы правила не текли за пределы сцены.
 */
export function buildTimelineCss(scope = '.dm-scene', stage = STAGE): string {
  const out: string[] = [camKeyframes('dmCamNew', stage)];

  // Такты ГРУППИРУЮТСЯ ПО СЕЛЕКТОРУ и склеиваются в один `animation` через
  // запятую. Отдельными правилами нельзя: `animation` — не складываемое
  // свойство, второе правило с тем же селектором снесло бы первое целиком
  // (элемент с появлением и вспышкой потерял бы появление).
  const bySel = new Map<string, Beat[]>();
  BEATS.forEach((b) => {
    const list = bySel.get(b.sel);
    if (list) list.push(b);
    else bySel.set(b.sel, [b]);
  });

  // Такты одного элемента не должны трогать ОДНО свойство: `animation` не
  // складывается, побеждает последняя анимация в списке, и предыдущая пропадает
  // молча. Эта ошибка уже дважды уходила в браузер (дубль маршрута курсора,
  // разделённые «появиться»/«уйти»), поэтому проверка стоит в самом генераторе.
  if (process.env.NODE_ENV !== 'production') {
    const PROPS: Record<BeatKind, string[]> = {
      rise: ['opacity', 'transform'],
      fade: ['opacity'],
      pop: ['opacity', 'transform'],
      slide: ['opacity', 'transform'],
      path: ['transform'],
      shift: ['transform'],
      type: ['width'],
      draw: ['opacity', 'transform'],
      grow: ['height'],
      ripple: ['opacity', 'transform'],
      collapse: ['opacity', 'transform'],
      glow: ['box-shadow'],
      flow: ['opacity', 'stroke-dashoffset'],
      wire: ['opacity', 'stroke-dashoffset'],
    };
    bySel.forEach((beats, sel) => {
      const seen = new Map<string, BeatKind>();
      beats.forEach((b) => {
        // `grow` правит ЛИБО высоту, ЛИБО ширину — смотря какие шаги задали.
        // Объявлять оба свойства нельзя: два такта, меняющие разные стороны
        // одной рамки, конфликтовали бы на пустом месте.
        const props = b.kind === 'grow' && b.ws ? ['width'] : PROPS[b.kind ?? 'rise'];
        props.forEach((prop) => {
          const prev = seen.get(prop);
          if (prev) {
            throw new Error(
              `Хореография: у «${sel}» два такта правят ${prop} `
              + `(${prev} и ${b.kind ?? 'rise'}). Второй отменит первый — объедините их.`,
            );
          }
          seen.set(prop, b.kind ?? 'rise');
        });
      });
    });
  }

  const rules: string[] = [];
  let n = 0;
  bySel.forEach((beats, sel) => {
    const names = beats.map((b) => {
      const name = `dmB${n++}`;
      out.push(beatKeyframes(name, b));
      return b.kind === 'flow'
        ? `${name} ${TOTAL}s linear infinite,${name}f 6s linear infinite`
        : `${name} ${TOTAL}s linear infinite`;
    });
    rules.push(`${scope} ${sel}{animation:${names.join(',')}}`);
  });
  return [...out, ...rules].join('\n');
}

/** Отладка стенда: подпись текущего кадра по времени. */
export function shotAt(t: number): Shot {
  let cur = SHOTS[0];
  for (const s of SHOTS) if (t >= s.at) cur = s;
  return cur;
}
